import {
  ControlContextProvider,
  IconPeopleCircle,
  MultiSelectControl,
  Pill,
  PopoverWrapper,
  type Option
} from '@sequencehq/core-components'
import { useSetAtom } from 'jotai'
import { ScopeProvider } from 'jotai-scope'
import {
  type FC,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useState
} from 'react'
import { match } from 'ts-pattern'
import type { ValidationResult } from 'modules/Cube/domain/cube.domain.types'
import { SidebarSelectControlTrigger } from 'Cube/view/layouts/quote/QuoteEditorSidebar/widgets/SidebarSelectControlTrigger'
import { PopoverRecipients } from '../PopoverRecipients/PopoverRecipients'
import { RecipientMenuItem } from './RecipientMenuItem'
import { recipientTypesAtom } from './recipientTypes.atoms'
import {
  type RecipientsValue,
  type RecipientType,
  useRecipientsSelection
} from './useRecipientsSelection'

type RecipientEditorControlType = 'sidebar' | 'pill'

export const RecipientsEditorControl: FC<{
  type: RecipientEditorControlType
  options: Option[]
  value: RecipientsValue
  onChange: (newValue: RecipientsValue) => void
  onCreate: () => void
  validationErrors: ValidationResult[]
  disabled: boolean
  'data-testid'?: string
}> = ({
  type,
  options,
  onChange,
  value,
  onCreate,
  validationErrors,
  disabled,
  'data-testid': dataTestId
}) => (
  <RecipientsMultiSelectControl
    type={type}
    options={options}
    value={value}
    onChange={onChange}
    disabled={disabled}
    validationErrors={validationErrors}
    placeholder="Recipients"
    icon={<IconPeopleCircle color="inherit" />}
    onCreate={onCreate}
    popover={<PopoverRecipients />}
    data-testid={dataTestId}
  />
)

type RecipientsMultiSelectControlProps = {
  type: RecipientEditorControlType
  value: RecipientsValue
  onChange: (newValue: RecipientsValue) => void
  disabled: boolean
  options: Option[]
  validationErrors: ValidationResult[]
  placeholder: string
  icon: ReactNode
  onCreate: () => void
  popover?: ReactNode
  'data-testid'?: string
}

export const RecipientsMultiSelectControl: FC<
  RecipientsMultiSelectControlProps
> = props => (
  <ScopeProvider atoms={[recipientTypesAtom]}>
    <RecipientsMultiSelectControlContent {...props} />
  </ScopeProvider>
)

const RecipientsMultiSelectControlContent: FC<
  RecipientsMultiSelectControlProps
> = ({
  type,
  value,
  onChange,
  disabled,
  options,
  validationErrors,
  placeholder,
  icon,
  onCreate,
  popover,
  'data-testid': dataTestId
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const setRecipientTypes = useSetAtom(recipientTypesAtom)
  const recipientsSelection = useRecipientsSelection(value)

  const errorMessage = validationErrors.length
    ? 'Select a recipient'
    : undefined
  const selectedLabel = recipientsSelection.selectedContacts.length
    ? (options.find(
        option => option.value === recipientsSelection.selectedContacts.at(0)
      )?.label ?? '')
    : undefined
  const more =
    recipientsSelection.selectedContacts.length < 2
      ? undefined
      : recipientsSelection.selectedContacts.length - 1

  const trigger = match(type)
    .with('pill', () => (
      <Pill
        interactive
        disabled={disabled}
        icon={icon}
        error={Boolean(errorMessage)}
        more={more}
        data-testid={dataTestId}
      >
        {recipientsSelection.selectedContacts.length
          ? selectedLabel
          : placeholder}
      </Pill>
    ))
    .with('sidebar', () => (
      <SidebarSelectControlTrigger
        isMenuOpen={isOpen}
        icon={icon}
        labelSelectedOption={selectedLabel}
        labelWhenNoSelection="No recipients"
        disabled={disabled}
        more={more}
        errorMessage={errorMessage}
        data-testid={dataTestId}
      />
    ))
    .exhaustive()

  const placement = match(type)
    .with('pill', () => 'bottom-start' as const)
    .with('sidebar', () => 'bottom-end' as const)
    .exhaustive()

  const handleSelectionChange = (selectedContacts: string[]) => {
    onChange(recipientsSelection.setSelected(selectedContacts))
  }

  const handleTypeChange = useCallback(
    (contactId: string, recipientType: RecipientType) => {
      onChange(recipientsSelection.setType(contactId, recipientType))
    },
    [onChange, recipientsSelection.setType]
  )

  useLayoutEffect(() => {
    setRecipientTypes({
      contactTypes: recipientsSelection.contactTypes,
      setType: handleTypeChange,
      disabled
    })
  }, [
    disabled,
    handleTypeChange,
    recipientsSelection.contactTypes,
    setRecipientTypes
  ])

  return (
    <ControlContextProvider
      placement={placement}
      matchWidth={false}
      maxWidth={408}
    >
      <MultiSelectControl
        placeholder="Recipients"
        options={options}
        values={recipientsSelection.selectedContacts}
        onChange={handleSelectionChange}
        trigger={
          popover && !isOpen ? (
            <PopoverWrapper popover={popover}>{trigger}</PopoverWrapper>
          ) : (
            trigger
          )
        }
        onToggle={setIsOpen}
        createLabel="New recipient"
        onCreate={() => {
          onCreate()
          setIsOpen(false)
        }}
        disabled={disabled}
        MenuItemComponent={RecipientMenuItem}
        data-testid={dataTestId}
      />
    </ControlContextProvider>
  )
}
